import { List as ImmutableList, type List } from 'immutable';
import { v4 as uuid } from 'uuid';

import { accountsSelectors } from 'companion-app-components/flux/accounts';
import store from 'companion-app-components/utils/redux-store';
import { transactionsTypes, transactionsUtils } from 'companion-app-components/flux/transactions';
import RootState from 'companion-app-components/utils/redux-store/rootState';
import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'companion-app-components/flux/chart-of-accounts';
import {
  memorizedRulesActions,
  memorizedRulesSelectors,
  memorizedRulesTypes,
} from 'companion-app-components/flux/memorized-rules';

import { isComplexRule } from 'containers/Settings/ChildPages/MemorizedPayee/utils';
import { SplitItemType } from './types';

export const UNCATEGORIZED_ID = '0';
export const TRANSFER_CATEGORY_ID = '21';
export const OLD_UNCATEGORIZED_ID = '20';
export const OLD_BALANCE_ADJUSTMENT = '1000';
const {
  BALANCE_ADJUSTMENT: BAL_ADJUSTMENT,
  OPENING_BALANCE,
  TRANSFER,
  CREDIT_CARD_PAYMENT,
} = chartOfAccountsTypes.BalanceAdjustmentTypeEnum;

export const INTERNAL_CATEGORIES = [
  OLD_UNCATEGORIZED_ID,       // '20'
  TRANSFER_CATEGORY_ID,       // '21'
  BAL_ADJUSTMENT,             // '0'
  OLD_BALANCE_ADJUSTMENT,     // '1000'
  OPENING_BALANCE,            // '1'
  TRANSFER,                   // '2'
  CREDIT_CARD_PAYMENT,        // '3'
];

type SplitItem = transactionsTypes.SplitItem;
type SplitItemProps = transactionsTypes.SplitItemProps;
const { mkMemorizedRule } = memorizedRulesTypes;
const { ACCOUNT, BALANCE_ADJUSTMENT, CATEGORY, UNCATEGORIZED } = chartOfAccountsTypes.CoaTypeEnum;


export function getSplitCategoryNames(
  txn: { split?: { items?: Array<any> | ImmutableList<any> | null } | null } | null | undefined,
) {
  const splitItems = txn?.split?.items;
  if (!splitItems) {
    return 'SPLIT';
  }
  const itemsArray = Array.isArray(splitItems) ? splitItems : (splitItems as ImmutableList<any>).toArray();
  return itemsArray
    .map((item) => chartOfAccountsSelectors.getCoaStringSelector(undefined, item?.coa, true))
    .filter(Boolean)
    .join(', ');
}

export const categoryCOAObject = (coa: chartOfAccountsTypes.ChartOfAccountProps) => {
  let { id, type } = coa || {};

  if (coa?.id === OLD_UNCATEGORIZED_ID || coa?.id === '') {
    // Uncategorized
    id = '0';
    type = UNCATEGORIZED;
  } else if (coa?.id === TRANSFER_CATEGORY_ID) {
    // Transfer
    type = ACCOUNT;
    id = '0';
  } else if (
    coa?.id === INTERNAL_CATEGORIES[3] || // Adjustment
    coa?.id === INTERNAL_CATEGORIES[4] || // Opening Balance
    coa?.id === INTERNAL_CATEGORIES[5] || // External Transfer
    coa?.id === INTERNAL_CATEGORIES[6]
  ) {
    // Credit Card Payment
    type = BALANCE_ADJUSTMENT;
    if (coa?.id === INTERNAL_CATEGORIES[3]) {
      id = '0';
    } else if (coa?.id === INTERNAL_CATEGORIES[4]) {
      id = '1';
    } else if (coa?.id === INTERNAL_CATEGORIES[5]) {
      id = '2';
    } else {
      id = '3';
    }
  }

  return { id, type };
};

export const getCOAType = (categoryId: string) => {
  let type;

  if (categoryId === UNCATEGORIZED_ID || categoryId === '20' || categoryId === '') {
    // Uncategorized
    type = UNCATEGORIZED;
  } else if (categoryId === TRANSFER_CATEGORY_ID) {
    // Transfer
    type = ACCOUNT;
  } else if (
    categoryId === INTERNAL_CATEGORIES[3] || // Adjustment
    categoryId === INTERNAL_CATEGORIES[4] || // Opening Balance
    categoryId === INTERNAL_CATEGORIES[5] || // External Transfer
    categoryId === INTERNAL_CATEGORIES[6]
  ) {
    // Credit Card Payment
    type = BALANCE_ADJUSTMENT;
  } else {
    type = CATEGORY;
  }

  return type;
};

const getCOAObject = (categoryId: string) => {
  const account = accountsSelectors.getAccountById(store?.getState(), categoryId);
  const coa = { id: categoryId, type: account ? ACCOUNT : getCOAType(categoryId) };
  return categoryCOAObject(coa);
};

export const checkSplitItemSize = (items: List<SplitItem> | Array<SplitItemProps>, size = 0) =>
  ('size' in items && items.size > size) || ('length' in items && items.length > size);

export const getMemorizedRule = (transaction: transactionsTypes.TransactionProps) => {
  const memorizedRule:memorizedRulesTypes.MemorizedRuleProps = {
    clientId: `${uuid().toUpperCase()}`,
    payee: transaction.payee ?? '',
    transactionType: transaction.amount > 0 ? 'DEPOSIT' : 'PAYMENT',
    memo: transaction.memo ?? '',
    amount: transaction.amount?.toString(),
    markAsCleared: transaction.state === 'CLEARED',
    tags: transaction?.tags || [],
    split: undefined,
  };
  //  if splits appends splits to the rule
  const splitItems = transaction.split?.items;
  let normalizedSplits: Array<SplitItemType> = [];
  if (Array.isArray(splitItems)) {
    normalizedSplits = splitItems as Array<SplitItemType>;
  } else if (splitItems && 'toArray' in splitItems) {
    normalizedSplits = (splitItems as any).toArray() as Array<SplitItemType>;
  }
  const filteredSplits = normalizedSplits.filter(({ amount = 0 }) => Math.abs(amount) > 0);
  if (filteredSplits && checkSplitItemSize(filteredSplits, 1)) {
    const splits = filteredSplits.map((split: Partial<SplitItemType>) => ({
      clientId: `${uuid()}`.toUpperCase(),
      amount: split.amount,
      memo: split?.memo,
      coa: split?.coa || getCOAObject(split?.coa?.id ? split?.coa?.id : UNCATEGORIZED_ID),
      tags: split?.tags || null,
    }));

    return {
      ...memorizedRule,
      split: { items: splits },
      tags: [],
    };
  } 

  // If not split append coa object
  const coa = transaction?.coa || getCOAObject(transaction?.coa?.id ? transaction?.coa?.id : UNCATEGORIZED_ID);
  return { ...memorizedRule, coa };
};

export const upsertMemorizedPayeeRule = (transactions: transactionsTypes.CashFlowTransaction[], state: RootState) => {

  if (transactions.length === 0) {
    return;
  }

  const memorizedRules: memorizedRulesTypes.MemorizedRuleProps[] = [];

  transactions.forEach((transaction) => {
    if (!transaction.payee || transaction.isDeleted) {
      return;
    }

    // check applied ruleId is available then use it ti fetch MemRule
    const rule = transaction.ruleId
      ? memorizedRulesSelectors.getMemorizedRuleById(state, transaction.ruleId)
      : null;

    if (rule) {
      if (!rule.isNotEditableByTransactions && !isComplexRule(rule)) {
        const memorizedRule = getMemorizedRule(transaction);
        memorizedRule.id = rule.id;
        memorizedRules.push(memorizedRule);
      }
    } else {
      // try find rules by payee name 
      const rulesByPayee = memorizedRulesSelectors.getMemRulesByPayeeName(state, transaction.payee ?? '');

      // check if any rule has same category as the transaction below 
      const sameCoaRule = rulesByPayee.find((payee) => {

        const isTxnSplit = transactionsUtils.isSplitTxn(transaction);
        const isRuleSplit = transactionsUtils.isSplitTxn(payee);

        // If both are not splits normal COA compare
        if (!isTxnSplit && !isRuleSplit) {
          return chartOfAccountsUtils.coasAreEqual(transaction.coa, payee.coa);
        }

        // if both are split compare the coa of splits
        if (isTxnSplit && isRuleSplit) {
          return transactionsUtils.splitCoaAreEqual(transaction.split!, payee.split);
        }

        // Either one of them is non split so, they are not equal
        return false;
      });

      // We must not memorize a txn which has same COA object as existing complex memorized rule
      // As QCS does not check for the same COA, it creates a new normal rule with same COA 
      if (sameCoaRule && isComplexRule(sameCoaRule)) {
        return;
      }

      // There can be only 1 normal rule, so find the normal from out of all rules with same payee name
      const normalRule = rulesByPayee.find((ruleItem) => !isComplexRule(ruleItem));

      // other than conditional rules only one rule is allowed irrespective of locked status
      // if any locked rule exists, do nothing. Because creating also updates the existing rule with lock.
      if (normalRule && normalRule.isNotEditableByTransactions) {
        return;
      }

      const memorizedRule = getMemorizedRule(transaction);
      if (normalRule) {
        memorizedRule.id = normalRule.id;
      }

      memorizedRules.push(memorizedRule);
    }
  });

  if (memorizedRules.length > 0) {
    store?.dispatch(memorizedRulesActions.combineMemorizedRules(memorizedRules.map(mkMemorizedRule)));
  }
};
